import { motion } from "framer-motion";
import { Reveal } from "./Reveal";

const ventures = [
  { name: "Mosley Music Group", year: "2006", v: "Home to OneRepublic and Nelly Furtado, with a catalog that has moved tens of millions." },
  { name: "Verzuz", year: "2020", v: "Co-founded with Swizz Beatz during the pandemic. The two reacquired full ownership and signed an exclusive distribution deal with X in 2024, keeping complete creative control." },
  { name: "Beatclub", year: "2021", v: "A marketplace connecting producers with major artists and labels, backed early by J. Cole, Mike WiLL Made-It and Justin Timberlake." },
  { name: "Timbaland Way Cares", year: "2025", v: "A registered 501(c)(3) nonprofit built on the belief that creativity can change a life. Through education, mentorship and community programs, it gives people a stage, a resource and a chance." },
];

function TextBlock({ overline, heading, children, delay = 0 }) {
  return (
    <Reveal delay={delay} className="grid md:grid-cols-12 gap-6 md:gap-10 border-t border-white/10 py-16 md:py-24">
      <div className="md:col-span-4">
        <p className="overline text-gold mb-4">{overline}</p>
        {heading && <h3 className="font-editorial italic text-3xl md:text-4xl leading-tight text-white">{heading}</h3>}
      </div>
      <div className="md:col-span-8 space-y-5 font-body text-white/65 text-lg md:text-xl leading-relaxed max-w-3xl">
        {children}
      </div>
    </Reveal>
  );
}

export default function OriginDeepDive() {
  return (
    <section className="relative bg-obsidian pb-8" data-testid="origin-deep-dive">
      <div className="absolute inset-0 pointer-events-none" aria-hidden style={{ background: "radial-gradient(circle at 12% 8%, rgba(79,176,74,0.10), transparent 55%)" }} />
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">

        <TextBlock overline="Before The Plaques" heading="The first origin was an accident.">
          <p>
            At fourteen, Tim Mosley was working a shift at a Norfolk Red Lobster when a coworker's gun went off. The
            bullet left him partially paralyzed for nine months. Stuck in a body that would not cooperate, he taught
            himself to DJ with the only hand that still worked, his left.
          </p>
          <p className="text-white/80">
            That is the real birth of the stutter. A producer who built an entire style around the beat refusing to
            move the way you expect it to.
          </p>
        </TextBlock>

        <TextBlock overline="The Scene That Raised A Sound" delay={0.05}>
          <p>
            He never did it alone. Late 1980s Virginia Beach was quietly incubating one of the most important music
            scenes in modern history. Teddy Riley was the local legend everyone measured themselves against. Pharrell
            Williams and Chad Hugo were forming what would become The Neptunes. Pusha T and Malice were friends from
            high school who would become Clipse. And Missy Elliott was the creative other half Tim would build his
            entire early catalog around.
          </p>
          <p>
            Jodeci's DeVanté Swing discovered Tim and Missy together and pulled them into the Swing Mob collective. It
            took a single Aaliyah demo to convince a label to sign them both in 1996. That same year, Ginuwine's "Pony"
            and Aaliyah's One in a Million landed, and the sound that put the 757 on the map was already rewriting the
            rules.
          </p>
        </TextBlock>

        <TextBlock overline="The Sound Nobody Else Was Making" heading="Silence, weaponized." delay={0.05}>
          <p>
            What made the production revolutionary was not only what he added, the baby coos, the clicks, the gasps,
            the tongue pops, the video game blips. It was what he left out.
          </p>
          <p className="text-white/80">
            He built beats around negative space and let silence do as much work as the 808. That skeletal, syncopated
            approach did not just define an era of R&B and hip-hop. It rewired what a whole generation of producers
            thought a beat was even allowed to sound like.
          </p>
        </TextBlock>

        <TextBlock overline="Right Now" heading="The chapter still being written." delay={0.05}>
          <p>
            Genre experiments most careers never attempt. Yellow (2024), a meditation album made with German handpan
            player Malte Marten, and Timbo Progression (2025), a largely instrumental Afrobeat record.
          </p>
          <p className="text-white/80">
            In 2026 the pace only picked up, with the launch of Cam Up Live (TimIdo), the Stage Zero label, and a run of
            mixtapes rolling out one by one with several major artists, the most recent arriving with Chloe Bailey.
          </p>
        </TextBlock>

        {/* Builder / Ventures */}
        <Reveal className="border-t border-white/10 pt-16 md:pt-24">
          <p className="overline text-gold mb-4">Builder As Much As Beatmaker</p>
          <h3 className="headline text-4xl md:text-6xl text-white mb-12">More than a producer. A founder.</h3>
          <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {ventures.map((v, i) => (
              <motion.div
                key={v.name}
                data-testid={`origin-venture-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: (i % 2) * 0.07 }}
                className="bg-obsidian p-8 md:p-10 hover:bg-white/[0.03] transition-colors"
              >
                <div className="flex items-baseline justify-between mb-3">
                  <h4 className="font-editorial italic text-2xl md:text-3xl text-white">{v.name}</h4>
                  <span className="font-display uppercase tracking-[0.16em] text-[0.66rem] text-gold">{v.year}</span>
                </div>
                <p className="font-body text-white/60 text-[0.95rem] leading-relaxed">{v.v}</p>
              </motion.div>
            ))}
          </div>
        </Reveal>

        <TextBlock overline="The Second Origin" heading="The comeback nobody talks about." delay={0.05}>
          <p>
            This is the part most bios leave out, and it is the most human one. After years of touring, pressure and a
            painkiller dependency that began with a 2011 prescription, Timbaland got sober on his own terms and rebuilt
            his health from the ground up, training in boxing and losing 130 pounds.
          </p>
          <p className="text-white/80">
            He talks about it openly, not as a low point to hide but as the chapter that reset everything after it. It
            belongs here because it is an origin too. The second one.
          </p>
        </TextBlock>

      </div>
    </section>
  );
}
